import { Injectable, inject, DOCUMENT } from '@angular/core';

/**
 * Service for managing scroll lock behavior across the application.
 * Prevents background scrolling while modals, drawers, or overlays are active.
 * Properly restores original inline styles when unlocking.
 *
 * Usage:
 * - Lock on overlay open: this.scrollLockService.lock()
 * - Unlock on overlay close: this.scrollLockService.unlock()
 */
@Injectable({
  providedIn: 'root',
})
export class ScrollLockService {
  private readonly document = inject(DOCUMENT);
  private readonly lockedElements = new Map<HTMLElement, Partial<CSSStyleDeclaration>>();

  /**
   * Lock scrolling on the body and optionally additional elements.
   * Stores original inline styles for restoration when unlocking.
   *
   * @param additionalElements Optional array of additional DOM elements to lock
   * @example
   * // Lock body only (default behavior)
   * this.scrollLockService.lock();
   *
   * // Lock body plus main layout scroll container
   * const mainLayout = document.querySelector<HTMLElement>('app-main-layout main');
   * this.scrollLockService.lock(mainLayout ? [mainLayout] : undefined);
   */
  lock(additionalElements?: HTMLElement[]): void {
    const bodyEl = this.document.body;
    const htmlEl = this.document.documentElement;

    // Lock HTML and body
    this.lockElement(htmlEl, {
      overflowY: 'hidden',
      overscrollBehaviorY: 'none',
      touchAction: 'none',
    });

    this.lockElement(bodyEl, {
      overflowY: 'hidden',
      overscrollBehaviorY: 'none',
      touchAction: 'none',
    });

    // Lock any additional elements passed in
    if (additionalElements && additionalElements.length > 0) {
      additionalElements.forEach((element) => {
        this.lockElement(element, {
          overflowY: 'hidden',
          overscrollBehaviorY: 'none',
          touchAction: 'none',
        });
      });
    }
  }

  /**
   * Unlock scrolling by restoring original inline styles.
   * Clears the internal tracking map after restoration.
   */
  unlock(): void {
    this.lockedElements.forEach((originalStyle, element) => {
      element.style.overflowY = originalStyle.overflowY ?? '';
      element.style.overscrollBehaviorY = originalStyle.overscrollBehaviorY ?? '';
      element.style.touchAction = originalStyle.touchAction ?? '';
    });

    this.lockedElements.clear();
  }

  /**
   * Lock a single element with specified styles.
   * Stores original styles for later restoration.
   *
   * @param element The DOM element to lock
   * @param nextStyle The styles to apply
   */
  private lockElement(element: HTMLElement, nextStyle: Partial<CSSStyleDeclaration>): void {
    // Store originals once per element; repeated calls should only update values.
    if (!this.lockedElements.has(element)) {
      this.lockedElements.set(element, {
        overflowY: element.style.overflowY,
        overscrollBehaviorY: element.style.overscrollBehaviorY,
        touchAction: element.style.touchAction,
      });
    }

    if (nextStyle.overflowY !== undefined) {
      element.style.overflowY = nextStyle.overflowY;
    }

    if (nextStyle.overscrollBehaviorY !== undefined) {
      element.style.overscrollBehaviorY = nextStyle.overscrollBehaviorY;
    }

    if (nextStyle.touchAction !== undefined) {
      element.style.touchAction = nextStyle.touchAction;
    }
  }
}
